/**
 * Verb conjugation: regular -ar / -er / -ir across the five tenses this build teaches
 * (BRIEF <spanish_correctness> layer 2, SPEC §2 `lang/conjugation.ts`).
 *
 * Pure. Tables, not a network call, not a dictionary lookup.
 *
 * **FIVE PERSONS, NOT SIX. There is no vosotros here and there never will be.** CLAUDE.md's
 * Spanish rule is unconditional — Mexican Spanish only, never vosotros — and TASKS T31e's
 * "six persons" is the task list disagreeing with an invariant, which the invariant wins. Mexican
 * Spanish uses `ustedes` for both the familiar and formal plural, so the second-person plural slot
 * does not exist rather than being suppressed at render time. A form that is never generated
 * cannot leak into content. See DECISIONS D-066.
 *
 * Scope: morphologically regular verbs, **including the orthographic adjustments that keep them
 * sounding regular** — `buscar` → `busqué`, not `buscé`. Those are not irregularities, they are
 * spelling doing its job, and a validator that marked `busqué` wrong would be worse than useless.
 * Genuinely irregular verbs are T31.
 */

// Explicit `.ts` extensions, as in `variety.ts`: `scripts/check-content.ts` imports this module
// under `node --experimental-strip-types` to verify that every conjugated form authored into a
// content file matches the table, and that resolver does no extensionless resolution.
// See DECISIONS D-070, D-074.
import { syllabify } from './accents.ts';
import { irregularForm, isIrregular } from './irregulars.ts';

export type Tense = 'present' | 'preterite' | 'imperfect' | 'subjunctive' | 'conditional';

/**
 * The five persons of Mexican Spanish.
 *
 * `ustedes` covers what Peninsular Spanish splits between `vosotros` and `ustedes`.
 */
export type Person = 'yo' | 'tu' | 'el' | 'nosotros' | 'ustedes';

export const PERSONS: readonly Person[] = ['yo', 'tu', 'el', 'nosotros', 'ustedes'];
export const TENSES: readonly Tense[] = [
  'present',
  'preterite',
  'imperfect',
  'subjunctive',
  'conditional',
];

/** Display labels. `tú` carries its accent here; the `Person` key stays ASCII. */
export const PERSON_LABELS: Readonly<Record<Person, string>> = {
  yo: 'yo',
  tu: 'tú',
  el: 'él / ella / usted',
  nosotros: 'nosotros',
  ustedes: 'ustedes / ellos / ellas',
};

export type VerbClass = 'ar' | 'er' | 'ir';

export type Conjugation = Record<Person, string>;
export type VerbTable = Record<Tense, Conjugation>;

// ---------------------------------------------------------------------------------------------
// Endings
// ---------------------------------------------------------------------------------------------

/**
 * Endings attached to the stem (infinitive minus its last two letters).
 *
 * `conditional` is the exception: it attaches to the **whole infinitive**, which is the
 * "-ría as a suffix on the infinitive" pattern the brief calls out as a constructive rule worth
 * teaching directly (BRIEF <conversion_engine>). It is stored here as a full-infinitive ending and
 * `conjugateRegular` knows not to strip the stem for it.
 */
const ENDINGS: Record<VerbClass, Record<Tense, Conjugation>> = {
  ar: {
    present: { yo: 'o', tu: 'as', el: 'a', nosotros: 'amos', ustedes: 'an' },
    preterite: { yo: 'é', tu: 'aste', el: 'ó', nosotros: 'amos', ustedes: 'aron' },
    imperfect: { yo: 'aba', tu: 'abas', el: 'aba', nosotros: 'ábamos', ustedes: 'aban' },
    subjunctive: { yo: 'e', tu: 'es', el: 'e', nosotros: 'emos', ustedes: 'en' },
    conditional: { yo: 'ía', tu: 'ías', el: 'ía', nosotros: 'íamos', ustedes: 'ían' },
  },
  er: {
    present: { yo: 'o', tu: 'es', el: 'e', nosotros: 'emos', ustedes: 'en' },
    preterite: { yo: 'í', tu: 'iste', el: 'ió', nosotros: 'imos', ustedes: 'ieron' },
    imperfect: { yo: 'ía', tu: 'ías', el: 'ía', nosotros: 'íamos', ustedes: 'ían' },
    subjunctive: { yo: 'a', tu: 'as', el: 'a', nosotros: 'amos', ustedes: 'an' },
    conditional: { yo: 'ía', tu: 'ías', el: 'ía', nosotros: 'íamos', ustedes: 'ían' },
  },
  ir: {
    // -er and -ir differ only in the present, and only in `nosotros`. Everything else is shared,
    // which is itself worth knowing and is why the tables are written out rather than merged.
    present: { yo: 'o', tu: 'es', el: 'e', nosotros: 'imos', ustedes: 'en' },
    preterite: { yo: 'í', tu: 'iste', el: 'ió', nosotros: 'imos', ustedes: 'ieron' },
    imperfect: { yo: 'ía', tu: 'ías', el: 'ía', nosotros: 'íamos', ustedes: 'ían' },
    subjunctive: { yo: 'a', tu: 'as', el: 'a', nosotros: 'amos', ustedes: 'an' },
    conditional: { yo: 'ía', tu: 'ías', el: 'ía', nosotros: 'íamos', ustedes: 'ían' },
  },
};

/** The conditional attaches to the infinitive, not the stem. */
const INFINITIVE_TENSES: ReadonlySet<Tense> = new Set<Tense>(['conditional']);

// ---------------------------------------------------------------------------------------------
// Infinitive shape
// ---------------------------------------------------------------------------------------------

export function verbClass(infinitive: string): VerbClass | null {
  const ending = infinitive.slice(-2).toLowerCase();
  return ending === 'ar' || ending === 'er' || ending === 'ir' ? ending : null;
}

export function stemOf(infinitive: string): string {
  return infinitive.slice(0, -2);
}

/**
 * Two letters is enough. `ir` is a whole infinitive, and rejecting it here made `iría` — which is
 * perfectly regular, the conditional attaching to the infinitive — throw instead. Found by the
 * T31a fall-through test.
 *
 * Its stem is empty, which is harmless: `ir` is irregular in every tense that attaches to a stem,
 * so only the conditional ever reaches the regular path.
 */
export function isRegularShape(infinitive: string): boolean {
  return verbClass(infinitive) !== null && infinitive.length >= 2;
}

// ---------------------------------------------------------------------------------------------
// Orthographic adjustment
// ---------------------------------------------------------------------------------------------

/**
 * Spelling changes that keep a regular verb sounding regular.
 *
 * Spanish spells the same consonant sound differently depending on the vowel that follows, so a
 * verb whose ending flips from an `a`/`o` vowel to an `e`/`i` one has to change its final stem
 * letter to keep the sound. `buscar` → `busqué`: the `c` would be soft before `é`, so it becomes
 * `qu`. None of this is irregularity — a learner who writes `buscé` has applied the rule correctly
 * and been let down by the orthography, and marking it wrong without naming why would be the exact
 * "just marked wrong" failure the brief forbids.
 *
 * Applied where the ending begins with the vowel that forces the change:
 *  - `-car`/`-gar`/`-zar`/`-guar` before `e` — preterite `yo`, and the whole subjunctive;
 *  - `-ger`/`-gir`/`-guir`, and `-cer`/`-cir` after a vowel, before `a`/`o` — present `yo`, and
 *    the whole subjunctive.
 */
function adjustStem(stem: string, klass: VerbClass, ending: string): string {
  const nextVowel = ending[0]?.toLowerCase() ?? '';
  const beforeE = nextVowel === 'e' || nextVowel === 'é';
  const beforeAO = nextVowel === 'a' || nextVowel === 'o' || nextVowel === 'á' || nextVowel === 'ó';
  const lower = stem.toLowerCase();

  if (klass === 'ar' && beforeE) {
    if (lower.endsWith('gu')) return `${stem.slice(0, -2)}gü`; // averiguar -> averigüé
    if (lower.endsWith('c')) return `${stem.slice(0, -1)}qu`; // buscar -> busqué
    if (lower.endsWith('g')) return `${stem}u`; // llegar -> llegué
    if (lower.endsWith('z')) return `${stem.slice(0, -1)}c`; // cruzar -> crucé
    return stem;
  }

  if ((klass === 'er' || klass === 'ir') && beforeAO) {
    if (lower.endsWith('gu')) return stem.slice(0, -1); // distinguir -> distingo
    if (lower.endsWith('g')) return `${stem.slice(0, -1)}j`; // proteger -> protejo
    if (lower.endsWith('c')) {
      const preceding = lower.at(-2) ?? '';
      // A vowel before the c gives the -zc- inceptive (conocer -> conozco); a consonant just
      // hardens it (vencer -> venzo).
      return 'aeiouáéíóú'.includes(preceding)
        ? `${stem.slice(0, -1)}zc`
        : `${stem.slice(0, -1)}z`;
    }
    return stem;
  }

  return stem;
}

/**
 * `i` between vowels becomes `y`: `leer` → `leyó`, `leyeron`; `construir` → `construyó`.
 *
 * Spanish does not write an unstressed `i` between two vowels, so an otherwise regular verb with a
 * vowel-final stem changes the ending rather than the stem.
 */
function adjustEnding(stem: string, ending: string): string {
  const last = stem.at(-1)?.toLowerCase() ?? '';
  if (!'aeiou'.includes(last)) return ending;
  if (ending === 'ió') return 'yó';
  if (ending === 'ieron') return 'yeron';
  return ending;
}

// ---------------------------------------------------------------------------------------------
// Conjugation
// ---------------------------------------------------------------------------------------------

/**
 * One regular form. Throws on an infinitive that is not `-ar`/`-er`/`-ir`, because a verb this
 * module cannot parse is a content bug and silently returning the input would hide it.
 */
export function conjugateRegular(infinitive: string, tense: Tense, person: Person): string {
  const klass = verbClass(infinitive);
  if (klass === null || !isRegularShape(infinitive)) {
    throw new RangeError(`"${infinitive}" is not an -ar/-er/-ir infinitive`);
  }

  const ending = ENDINGS[klass][tense][person];
  if (INFINITIVE_TENSES.has(tense)) return infinitive + ending;

  const stem = stemOf(infinitive);
  return adjustStem(stem, klass, ending) + adjustEnding(stem, ending);
}

/**
 * THE public entry point: the correct form, irregular or regular.
 *
 * Irregulars are consulted first and fall through per (tense, person), so a verb that is irregular
 * in the present and regular in the imperfect gets both right without either table knowing about
 * the other.
 */
export function conjugate(infinitive: string, tense: Tense, person: Person): string {
  return irregularForm(infinitive, tense, person) ?? conjugateRegular(infinitive, tense, person);
}

/** Whether this verb departs from the regular paradigm anywhere. */
export function hasIrregularForms(infinitive: string): boolean {
  return isIrregular(infinitive.toLowerCase());
}

export function conjugateTense(infinitive: string, tense: Tense): Conjugation {
  return {
    yo: conjugate(infinitive, tense, 'yo'),
    tu: conjugate(infinitive, tense, 'tu'),
    el: conjugate(infinitive, tense, 'el'),
    nosotros: conjugate(infinitive, tense, 'nosotros'),
    ustedes: conjugate(infinitive, tense, 'ustedes'),
  };
}

export function conjugateAll(infinitive: string): VerbTable {
  return {
    present: conjugateTense(infinitive, 'present'),
    preterite: conjugateTense(infinitive, 'preterite'),
    imperfect: conjugateTense(infinitive, 'imperfect'),
    subjunctive: conjugateTense(infinitive, 'subjunctive'),
    conditional: conjugateTense(infinitive, 'conditional'),
  };
}

// ---------------------------------------------------------------------------------------------
// Recognition
// ---------------------------------------------------------------------------------------------

export interface FormMatch {
  tense: Tense;
  person: Person;
}

/**
 * Every regular (tense, person) slot of `infinitive` whose form equals `candidate`.
 *
 * Returns all of them, because Spanish is genuinely ambiguous here and pretending otherwise would
 * mis-name the error: `hablamos` is present *and* preterite, and `hablaba` is `yo` *and* `él`. The
 * near-miss classifier (T33) needs the whole set to decide whether the learner picked the wrong
 * person or the wrong tense.
 */
export function identifyForm(infinitive: string, candidate: string): FormMatch[] {
  const wanted = candidate.trim().toLowerCase();
  const out: FormMatch[] = [];
  for (const tense of TENSES) {
    for (const person of PERSONS) {
      if (conjugate(infinitive, tense, person).toLowerCase() === wanted) {
        out.push({ tense, person });
      }
    }
  }
  return out;
}

/**
 * Whether a form is stressed on the stem rather than the ending.
 *
 * Used by the feedback card to explain why `hablábamos` carries a tilde. Derived from the
 * syllable count rather than hard-coded per tense, so it stays true for any stem length.
 */
export function stressFallsOnStem(infinitive: string, tense: Tense, person: Person): boolean {
  const form = conjugateRegular(infinitive, tense, person);
  const stemSyllables = syllabify(stemOf(infinitive)).length;
  const { length } = syllabify(form);
  return length > 0 && stemSyllables > 0 && length - stemSyllables <= 0;
}
